import React, { useContext, useState, useMemo, useRef } from 'react'
import isEmpty from 'lodash/isEmpty'
import { FixedSizeList as List } from 'react-window'
import { useTranslation } from 'react-i18next'
import InfiniteLoader from 'react-window-infinite-loader'

import { DataTableContext } from './DataTable'
import { StyledTableBody, StyledTable, NoData } from './StyledDataTable'
import TableFooter from './TableFooter'
import TableHeader from './TableHeader'
import TableRows from './TableRows'

const TableBody = props => {
	const { rowHeight, rows, width, height, withSelectRow, onSelect, prepareRow } = useContext(DataTableContext)
	const [selectedRow, setSelectedRow] = useState(null)

	const rowSelectHandler = (row) => {
		if (withSelectRow) {
			setSelectedRow(row.id)
		}
		if(onSelect) {
			onSelect(row.original)
		}
	}

	const preparedRows = useMemo(() => rows.map(row => {
		prepareRow(row)
		return row
	}), [rows, prepareRow])


	const { loading, withSearch, withFooter } = useContext(DataTableContext)
	const [t] = useTranslation()
	const calculateHeight = () => {
		let search = 0
		let footer = 0
		if (withSearch) {
			search = 40
		}
		if (withFooter) {
			footer = 36
		}
		return height - search - footer - 36
	}

	const dataWidth = preparedRows[0]?.cells ? rows[0].cells.reduce((prev, curr) => (prev + curr.column.width), 0) : width

	let tableStyles = { width: `${width}px` }
	let tableContentStyles = { display: 'flex', flexDirection: 'column', overflow: 'auto' }
	let scrollableTablebodyStyles = {}

	if (dataWidth > width) {
		tableStyles = Object.assign(tableStyles, { overflow: 'auto' })
		tableContentStyles = Object.assign(tableContentStyles, { width: `${dataWidth}px` })
		scrollableTablebodyStyles = Object.assign(scrollableTablebodyStyles, { overflowX: 'hidden', overflowY: 'auto' })
	}

	// Infinite loader
	const { error, hasNextPage, hasPreviousPage, loadNextPage, loadPreviousPage } = useContext(DataTableContext)
	const itemCount = rows.length
	const loaderRef = useRef()
	const dataLoadOffset = 50

	const isItemLoaded = index => {
		return (!hasNextPage || index < itemCount - dataLoadOffset) &&
		(!hasPreviousPage || index > dataLoadOffset)
	}

	const loadMoreItems = (startIndex, stopIndex) => {
		const isLoadingNextPage = stopIndex > itemCount - dataLoadOffset
		const isLoadingPreviousPage = startIndex < dataLoadOffset

		if(isLoadingNextPage && loadNextPage && !loading && !error) {
			loadNextPage()
		}
		if(isLoadingPreviousPage && loadPreviousPage && !loading && !error) {
			loadPreviousPage().then((res) => {
				if(loaderRef.current) {
					const listRef = loaderRef.current._listRef
					const newRecordsNumber = loaderRef.current.props.itemCount - itemCount
					listRef.scrollTo(listRef.state.scrollOffset + newRecordsNumber * 36)
				}
			})
		}
	}

	return (
		<StyledTable {...props} style={tableStyles}>
			<div style={tableContentStyles}>
				<TableHeader noData={isEmpty(rows)} style={{ width: `${dataWidth}px` }}/>
				{!isEmpty(rows)
					? <StyledTableBody rowHeight={rowHeight}>
						<InfiniteLoader
							ref={loaderRef}
							isItemLoaded={isItemLoaded}
							itemCount={itemCount}
							loadMoreItems={loadMoreItems}
						>
							{({ onItemsRendered, ref }) => {
								return (
									<List
										ref={ref}
 										onItemsRendered={onItemsRendered}
										itemCount={itemCount}
										height={calculateHeight()}
										itemSize={36}
										style={scrollableTablebodyStyles}
									>
										{({ index, style }) => (
											<TableRows
												index={index}
												rowStyle={style}
												selectedRow={selectedRow}
												rowSelectHandler={rowSelectHandler}
											/>
										)}
									</List>
								)
							}}
						</InfiniteLoader>
					</StyledTableBody>
					: <NoData>	{(!loading ? t('common.strings.table_no_data') : '')} </NoData>}
				{withFooter && <TableFooter style={{ width: `${dataWidth}px` }}/>}
			</div>
		</StyledTable>
	)
}

export default TableBody
